import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit
} from '@angular/core';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { EmptyChartComponent } from '@pages/dashboards/shared/empty-chart/empty-chart.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { EpdV2Service } from '@pages/dashboards/epd-v2/services/epd-v2.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as Highcharts from 'highcharts';
import { EPD_CHG_CHART_OPTIONS } from '@pages/dashboards/epd-v2/utils/chart-options';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DropdownMenuComponent, LoaderModule } from '@mds/ui';
import { finalize } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'app-epd-chg-ingredients',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    HighchartsChartModule,
    MatTooltipModule,
    LoaderModule,
    EmptyChartComponent,
    DropdownMenuComponent
],
  templateUrl: './epd-chg-ingredients.component.html',
  styleUrls: ['./epd-chg-ingredients.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EpdChgIngredientsComponent implements OnInit, OnChanges {
  @Input() materialsBreakdown: { rid: string; layerRid: string };
  @Input() layers: any[] = [];
  @Input() selectedLayer!: any;
  private epdService = inject(EpdV2Service);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  chartCallback: any;
  highcharts = Highcharts;
  chartOptions: Highcharts.Options;
  emptyChart = { title: 'No Data', description: 'No data available' };
  isEmpty = false;
  name!: string;
  unit!: string;
  loading = true;
  layerFormControl!: FormControl;
  layerOptions = [];
  constructor() {
    this.chartCallback = (chart: Highcharts.Chart) => {
      setTimeout(() => {
        chart.reflow();
      }, 0);
    };
  }

  ngOnChanges(): void {
    this.init();
    if (this.layers.length) {
      this.layerOptions =
        (this.layers || []).map((layer) => ({
          text: layer.name ?? '',
          value: layer?.rid?.replace('#', '')
        })) ?? [];
    }
  }
  ngOnInit(): void {
    this.layerFormControl = new FormControl([{ text: this.selectedLayer.name, value: this.selectedLayer.rid }]);
    this.layerFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      this.materialsBreakdown.layerRid = res[0].value;
      this.init();
    });
  }
  private init() {
    if (!this.materialsBreakdown.rid || !this.materialsBreakdown.layerRid) {
      return;
    }
    this.loading = true;
    this.epdService
      .fetchMaterialBreakdown(this.materialsBreakdown.rid, this.materialsBreakdown.layerRid)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data: { lines: any[]; name: string; unit: string }) => {
        this.name = data.name;
        this.unit = data.unit;
        const chartData = [];
        (data.lines || []).forEach((line) => {
          chartData.push({ y: isNaN(line.impact) ? 0 : Number(line.impact), name: line.label });
        });
        const total = chartData.reduce((sum, { y }) => sum + y, 0);
        chartData.forEach((li) => {
          if (li.y) li.y = Math.max((li.y / total) * 100, 0.5);
        });

        this.isEmpty = !total || !chartData.length;
        this.chartOptions = cloneDeep(EPD_CHG_CHART_OPTIONS(total, chartData));
        this.loading = false;
        this.cdr.markForCheck();
      });
  }
}
